import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { Package, ArrowLeft, CheckCircle, Buildings, Info } from "@phosphor-icons/react";
import AddressAutocomplete from "@/components/AddressAutocomplete";

import { PUBLIC_BACKEND as BACKEND } from "@/lib/api";
import { COUNTRIES as _COUNTRIES_RAW } from "@/lib/countries";

// Re-shape to {code, label} for this form's Select
const COUNTRIES = _COUNTRIES_RAW.map((c) => ({
  code: c.code,
  label: c.flag ? `${c.flag} ${c.name}` : c.name,
}));

const TRN_RE = /^\d{9}$/;

export default function SignupCustomer() {
  const { slug } = useParams();
  const [courier, setCourier] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    password_confirm: "",
    phone: "",
    country: "JM",
    address: "",
    address_lat: null,
    address_lng: null,
    trn: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    axios
      .get(`${BACKEND}/api/public/courier/${slug}`)
      .then((r) => setCourier(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (form.password !== form.password_confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (form.country === "JM" && !form.trn.trim()) {
      toast.error("TRN# is required for customers in Jamaica");
      return;
    }
    if (form.country === "JM" && form.trn.trim() && !TRN_RE.test(form.trn.trim())) {
      toast.error("TRN# must be exactly 9 digits");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone || null,
        country: form.country === "OTHER" ? null : form.country,
        address: form.address || null,
        address_lat: form.address_lat,
        address_lng: form.address_lng,
        trn: form.country === "JM" ? form.trn.trim() : null,
      };
      const { data } = await axios.post(`${BACKEND}/api/public/courier/${slug}/signup`, payload);
      setSuccess(data);
      toast.success(`Welcome to ${data.company_name}!`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Signup failed";
      toast.error(typeof detail === "string" ? detail : "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="courier-not-found">
        <div className="max-w-md w-full bg-white border border-zinc-200 rounded-xl p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 border border-red-200 mx-auto flex items-center justify-center mb-4">
            <Buildings size={24} weight="bold" className="text-red-600" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2" style={{ fontFamily: "Work Sans" }}>Courier not found</h1>
          <p className="text-zinc-600 mb-6">The link you followed doesn't match any active courier company on Kanal. Please check with your courier for the correct signup URL.</p>
          <Link to="/"><Button variant="outline" className="w-full">Back to Kanal</Button></Link>
        </div>
      </div>
    );
  }

  if (!courier) {
    return <div className="min-h-screen bg-zinc-50 flex items-center justify-center text-sm text-zinc-500" data-testid="courier-loading">Loading courier…</div>;
  }

  const logoUrl = courier.logo_url ? `${BACKEND}${courier.logo_url}` : null;

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="signup-customer-success">
        <div className="max-w-md w-full bg-white border border-zinc-200 rounded-xl p-8 text-center shadow-sm">
          <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 mx-auto flex items-center justify-center mb-5">
            <CheckCircle size={26} weight="bold" className="text-emerald-600" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2" style={{ fontFamily: "Work Sans" }}>Almost there — verify your email</h1>
          <p className="text-zinc-600 mb-2">
            Welcome to <b>{success.company_name}</b>. Your customer number is{" "}
            <code className="bg-zinc-100 px-1.5 py-0.5 rounded font-mono text-zinc-900">{success.customer_number}</code>.
          </p>
          <p className="text-sm text-zinc-600 mb-6">
            We&apos;ve emailed a verification link to <b>{form.email}</b>.{" "}
            <span className="font-medium text-zinc-900">Please click the link before signing in</span> — your account stays locked until your email is verified.
          </p>
          <Link to={`/c/${slug}/login`}>
            <Button className="w-full bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="customer-success-signin-btn">Go to sign in</Button>
          </Link>
        </div>
      </div>
    );
  }

  const inJamaica = form.country === "JM";
  // Restrict Google address autocomplete to the chosen country (empty = global)
  const acCountry = form.country && form.country !== "OTHER" ? form.country : "";

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="signup-customer-page">
      <Toaster position="top-right" />
      <div className="max-w-md mx-auto px-6 py-10">
         

        {/* Courier branding */}
        <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-5 text-center" data-testid="courier-brand-card">
          {logoUrl ? (
            <img src={logoUrl} alt={courier.name} className="h-16 mx-auto object-contain mb-4" data-testid="courier-logo" />
          ) : (
            <div className="h-16 w-16 mx-auto bg-[#002BF6] text-white rounded-lg flex items-center justify-center mb-4">
              <Buildings size={28} weight="bold" />
            </div>
          )}
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-1">Sign up as a customer of</div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 mb-1" style={{ fontFamily: "Work Sans" }} data-testid="courier-name">{courier.name}</h1>
          {courier.phone && <div className="text-xs text-zinc-500">{courier.phone}</div>}
        </div>

        <form onSubmit={onSubmit} className="bg-white border border-zinc-200 rounded-xl p-6 space-y-4 shadow-sm" data-testid="signup-customer-form">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 mb-2 w-fit">
            <Package size={11} weight="bold" /> Customer signup
          </div>

          <Field label="Full name" testid="customer-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          <Field label="Email" type="email" testid="customer-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
          <Field label="Phone (optional)" testid="customer-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />

          {/* Country */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Country <span className="text-red-500 ml-0.5">*</span></Label>
            <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v, trn: v === "JM" ? form.trn : "" })}>
              <SelectTrigger className="mt-1.5" data-testid="customer-country"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`customer-country-${c.code}`}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Address (Google autocomplete) */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Address</Label>
            <div className="mt-1.5">
              <AddressAutocomplete
                value={form.address}
                onChange={(v) => setForm((f) => ({ ...f, address: v }))}
                onLocationChange={(lat, lng) => setForm((f) => ({ ...f, address_lat: lat, address_lng: lng }))}
                country={acCountry}
                placeholder={inJamaica ? "Start typing a Jamaican address…" : "Start typing your delivery address…"}
                rows={2}
                dataTestId="customer-address"
                className="text-sm"
              />
            </div>
            {form.address_lat && form.address_lng && (
              <div className="text-[10px] text-emerald-700 mt-1.5 flex items-center gap-1" data-testid="customer-address-geocoded">
                <CheckCircle size={11} weight="bold" /> Geocoded: {form.address_lat.toFixed(4)}, {form.address_lng.toFixed(4)}
              </div>
            )}
          </div>

          {/* TRN — Jamaica only */}
          {inJamaica && (
            <div data-testid="customer-trn-section">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                TRN# <span className="text-red-500 ml-0.5">*</span>
              </Label>
              <Input
                type="text"
                inputMode="numeric"
                className="mt-1.5"
                value={form.trn}
                onChange={(e) => {
                  // Restrict to digits only, max 9 chars
                  const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 9);
                  setForm({ ...form, trn: digitsOnly });
                }}
                placeholder="123456789"
                maxLength={9}
                pattern="\d{9}"
                title="TRN must be exactly 9 digits"
                data-testid="customer-trn"
                required
              />
              <div className="flex items-start gap-1.5 text-[11px] text-zinc-500 mt-1.5">
                <Info size={11} className="mt-0.5 shrink-0" />
                <span>Required by Jamaican Customs for parcel clearance. Must be exactly 9 digits. Your TRN# is encrypted and masked on package documents.</span>
              </div>
            </div>
          )}

          <Field label="Choose a password (min 6 chars)" type="password" testid="customer-password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required />
          <Field label="Confirm password" type="password" testid="customer-password-confirm" value={form.password_confirm} onChange={(v) => setForm({ ...form, password_confirm: v })} required />

          <Button type="submit" disabled={submitting} className="w-full bg-[#002BF6] hover:bg-[#0021C7] text-white h-11" data-testid="customer-signup-btn">
            {submitting ? "Creating account…" : `Sign up with ${courier.name}`}
          </Button>
          <p className="text-[11px] text-zinc-500 text-center">
            Already have an account? <Link to="/login" className="text-[#002BF6] hover:underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required = false }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>
      <Input type={type} className="mt-1.5" value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid} />
    </div>
  );
}
